/**
 * Use-case model types — the context-first product model.
 *
 * ORGANIZATION → linked systems → PROJECT → project-bound context →
 * primary goal + additional use cases → agents/workflows → governance →
 * controlled execution.
 *
 * A use case is a configuration package: which user goals, workflows,
 * dashboard sections, readiness checks and recommended defaults are central
 * for a project. It is NEVER an authorization mechanism — selecting a use
 * case grants no permissions, bypasses no RLS, allows no external writes,
 * and lowers no approval rules. Capabilities are always re-derived from real
 * configuration: bindings + provider health + permissions + runtime support.
 */

import type { ContextMode } from "@/lib/workspace";
import type { ProjectBindingPurpose } from "@/lib/project-bindings";
import type { WorkProfile } from "@/lib/navigation/work-profiles";
import type { ProjectCapabilityId } from "./capabilities";
import type { ProjectSectionComponent } from "./dashboard";
import type { SetupWizardKind } from "@/lib/project-setup/wizard-launch";

/** Built-in project goals. Validated in the application layer on purpose —
 *  the DB column (`projects.primary_goal`, mig 213) has no CHECK so a future
 *  use case never needs a schema migration. HR is deliberately absent: it is
 *  not implemented, and the registry refuses to present what does not run. */
export const PROJECT_GOALS = [
  "support",
  "engineering",
  "deployment",
  "security",
  "knowledge",
  "custom",
] as const;
export type ProjectGoal = (typeof PROJECT_GOALS)[number];

export function isProjectGoal(v: unknown): v is ProjectGoal {
  return typeof v === "string" && (PROJECT_GOALS as readonly string[]).includes(v);
}

/** How remediation work starts for a project. This is a READ MODEL derived
 *  from the real runtime switches (support_desk gates auto-orchestration in
 *  src/server/orchestration/auto-start.ts) — it is used for display and for
 *  recommended defaults, never enforced from here. */
export const REMEDIATION_START_MODES = [
  "automatic_on_intake",
  "manual",
  "disabled",
] as const;
export type RemediationStartMode = (typeof REMEDIATION_START_MODES)[number];

/** Honest availability of a use case, based on what actually runs today. */
export const USE_CASE_AVAILABILITY = [
  "available",
  "beta",
  "preview",
  "hidden",
] as const;
export type UseCaseAvailability = (typeof USE_CASE_AVAILABILITY)[number];

/** Per-project activation status (project_use_cases.status, mig 213). */
export const USE_CASE_STATUSES = ["active", "disabled"] as const;
export type UseCaseStatus = (typeof USE_CASE_STATUSES)[number];

/** A use-case manifest. Everything a central resolver needs to present the
 *  use case — no scattered `if (useCase === …)` branches elsewhere. */
export type UseCaseDefinition = {
  /** Stable registry id; the primary use case's id doubles as the project's
   *  primary_goal value. */
  id: string;
  version: number;

  /** i18n keys (EN+NL in lockstep via the Dictionary type). */
  labelKey: string;
  shortDescriptionKey: string;
  fullDescriptionKey: string;
  primaryOutcomeKey: string;

  availability: UseCaseAvailability;

  /** Which user work profiles this use case is most relevant for (UX
   *  ordering only — never visibility-as-authorization). */
  applicableWorkProfiles: WorkProfile[];

  /** Capabilities the use case needs to be meaningfully ready / benefits
   *  from. Must reference the honest project capability catalogue
   *  (src/lib/use-cases/capabilities.ts) — unknown refs are refused at
   *  registration. */
  requiredCapabilities: ProjectCapabilityId[];
  recommendedCapabilities: ProjectCapabilityId[];

  /** Binding purposes worth suggesting during setup (subset of the real
   *  PROJECT_BINDING_PURPOSES union). */
  suggestedBindingPurposes: ProjectBindingPurpose[];

  /** Recommended safe defaults — presets only. Applying them goes through
   *  the normal permission-checked project APIs. */
  defaultExecutionMode?: ContextMode;
  defaultRemediationStartMode?: RemediationStartMode;

  /** Ids consumed by the central resolvers (onboarding, dashboard,
   *  readiness). New use cases register ids; the resolvers stay generic. */
  onboardingStepIds: string[];
  dashboardSectionIds: string[];
  readinessCheckIds: string[];

  /** Optional recommended governance template id
   *  (src/lib/governance/templates.ts). */
  defaultGovernanceProfileId?: string;

  /** Optional plan feature this use case belongs to (PLAN_FEATURES tenant
   *  setting, src/server/entitlements/plan-features.ts — "engineering" /
   *  "devops"). Commercial relevance signal ONLY: entitlements stay
   *  enforced at their existing chokepoints (mission start, reply
   *  approval), never by the registry. */
  requiredPlanFeature?: string;

  /** i18n keys explaining prerequisites in plain language. */
  prerequisiteDescriptionKeys: string[];
};

/** Readiness status vocabulary shared by all readiness checks. */
export const READINESS_STATUSES = [
  "ready",
  "missing",
  "degraded",
  "not_applicable",
  "optional",
] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number];

/**
 * Evidence the UI formats — never prose the resolver invented.
 *
 * The resolvers are pure and run on the server; they know nothing about the
 * reader's locale. So they emit a dictionary KEY plus its interpolation
 * params, and every surface that shows the same fact (the project page's
 * setup progress and context summary, the Command Center project block)
 * formats it from the SAME key. That is what killed "1 codebase(s)": a
 * countable fact emits the `…One` / `…Other` pair below, so EN and NL each
 * get a real singular and plural instead of an English "(s)" suffix that is
 * not even English.
 *
 * Keys live under `useCases.evidence.*` in `src/i18n/locales/{en,nl}.ts`.
 * Params carry only counts and already-sanitized short strings — never a
 * raw provider error or a secret.
 */
export type EvidenceMessage = {
  key: string;
  params?: Record<string, string | number>;
};

const EVIDENCE_KEY_PREFIX = "useCases.evidence";

/** Evidence with no countable part: `evidence("sandboxUnavailable")`. */
export function evidence(
  id: string,
  params?: Record<string, string | number>,
): EvidenceMessage {
  return { key: `${EVIDENCE_KEY_PREFIX}.${id}`, params };
}

/**
 * Countable evidence: picks the `…One` / `…Other` form by `count` and passes
 * `count` as a param. The resolver chooses the form because it is the only
 * place that has the number; the dictionary owns the words.
 */
export function evidenceCount(
  id: string,
  count: number,
  extra?: Record<string, string | number>,
): EvidenceMessage {
  return evidence(`${id}${count === 1 ? "One" : "Other"}`, { count, ...extra });
}

/**
 * How much a readiness check matters for the project to do its job — a
 * separate axis from `status`.
 *
 * `status` says what IS ("missing", "optional" = absent but legitimately so);
 * `importance` says what that MEANS for the verdict:
 *   - `required`    — not ready ⇒ the project needs setup
 *   - `recommended` — not ready ⇒ shown as a recommendation, never blocking
 *   - `optional`    — never a failure; listed under "More options"
 */
export const READINESS_IMPORTANCES = ["required", "recommended", "optional"] as const;
export type ReadinessImportance = (typeof READINESS_IMPORTANCES)[number];

/**
 * Where a check is set up INSIDE the project: the section page, and — when
 * one exists — the setup wizard that opens over it (`?setup=<kind>`, see
 * src/lib/project-setup/wizard-launch.ts). `actionHref` stays for the
 * Command Center and older links; the project page uses this.
 */
export type ReadinessSetupDescriptor = {
  section: ProjectSectionComponent;
  wizard?: SetupWizardKind;
};

export type ReadinessCheckResult = {
  id: string;
  status: ReadinessStatus;
  titleKey: string;
  descriptionKey: string;
  /** How much this check matters (see `ReadinessImportance`). Absent on
   *  checks registered without one — read as `required`, so an
   *  unclassified gap still blocks rather than quietly passing. */
  importance?: ReadinessImportance;
  /** Where to set it up inside the project. */
  setup?: ReadinessSetupDescriptor;
  /** Configured, but nothing has proved it works yet (recorded health is
   *  absent/unknown). Shown as "Not tested yet", never as working. */
  unverified?: boolean;
  /** Where to fix it (settings/config page). */
  actionHref?: string;
  /** Safe, already-sanitized short evidence summary (counts, capability
   *  names, health statuses) — never raw provider errors or secrets. */
  evidence?: EvidenceMessage;
};
